import { Scrape } from './scrapeDatabase';

/**
 * Sort scrapes by column and direction
 */
export const sortScrapes = (
  scrapes: Scrape[],
  sortColumn: string,
  sortDirection: 'asc' | 'desc'
): Scrape[] => {
  if (!scrapes.length) return [];
  
  const sortedScrapes = [...scrapes].sort((a, b) => {
    let valueA, valueB;
    
    // Extract the values to compare based on the sort column
    switch (sortColumn) {
      case "slug":
        valueA = a.slug?.toLowerCase() || "";
        valueB = b.slug?.toLowerCase() || "";
        break;
      case "status":
        valueA = a.status?.toLowerCase() || "";
        valueB = b.status?.toLowerCase() || "";
        break;
      case "timestamp":
        valueA = new Date(a.timestamp).getTime();
        valueB = new Date(b.timestamp).getTime();
        break;
      case "verified":
        // For verified column, we want to sort: true (valid) > false (invalid) > null (not verified)
        // Convert boolean/null to numeric values for sorting
        valueA = a.verified === true ? 2 : a.verified === false ? 1 : 0;
        valueB = b.verified === true ? 2 : b.verified === false ? 1 : 0;
        break;
      default:
        valueA = a[sortColumn as keyof Scrape] || "";
        valueB = b[sortColumn as keyof Scrape] || "";
    }
    
    // Compare the values based on sort direction
    if (sortDirection === "asc") {
      return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
    } else {
      return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
    }
  });
  
  return sortedScrapes;
};

/**
 * Filter scrapes by error state or invalid state
 */
export const filterScrapes = (
  scrapes: Scrape[], 
  showErrorsOnly: boolean, 
  showInvalidOnly: boolean = false,
  showUnverifiedOnly: boolean = false,
  hideNoPricing: boolean = true
): Scrape[] => {
  let filteredScrapes = scrapes;
  
  if (showErrorsOnly) {
    filteredScrapes = filteredScrapes.filter(scrape => scrape.status === 'error');
  }
  
  if (showInvalidOnly) {
    filteredScrapes = filteredScrapes.filter(scrape => isInvalidScrape(scrape));
  }
  
  if (showUnverifiedOnly) {
    filteredScrapes = filteredScrapes.filter(scrape => scrape.verified === null);
  }
  
  if (hideNoPricing) {
    filteredScrapes = filteredScrapes.filter(scrape => !scrape.no_pricing);
  }
  
  return filteredScrapes;
};

/**
 * Check if a scrape is considered invalid
 * Invalid scrapes are those with verified === false (shows as "Invalid" in Status column)
 */
export const isInvalidScrape = (scrape: Scrape): boolean => {
  return scrape.verified === false;
};

/**
 * Get count of invalid scrapes
 */
export const getInvalidCount = (scrapes: Scrape[]): number => {
  return scrapes.filter(scrape => isInvalidScrape(scrape)).length;
};

/**
 * Get count of no pricing scrapes
 */
export const getNoPricingCount = (scrapes: Scrape[]): number => {
  return scrapes.filter(scrape => scrape.no_pricing === true).length;
};

/**
 * Get unique statuses from scrapes
 */
export const getUniqueStatuses = (scrapes: Scrape[]): string[] => {
  return Array.from(new Set(scrapes.map(scrape => scrape.status).filter(Boolean)));
};

/**
 * Get unique scrapers from scrapes
 */
export const getUniqueScrapers = (scrapes: Scrape[]): string[] => {
  return Array.from(new Set(scrapes.map(scrape => scrape.scraper).filter(Boolean)));
};

/**
 * Get verification counts
 */
export const getVerificationCounts = (scrapes: Scrape[]) => {
  const verified = scrapes.filter(s => s.verified === true).length;
  const invalid = scrapes.filter(s => s.verified === false).length;
  const unverified = scrapes.filter(s => s.verified === null).length;
  
  return { verified, invalid, unverified };
};

/**
 * Get status counts
 */
export const getStatusCounts = (scrapes: Scrape[]) => {
  const ready = scrapes.filter(s => s.status === 'ready').length;
  const error = scrapes.filter(s => s.status === 'error').length;
  const running = scrapes.filter(s => s.status === 'running').length;
  const invalid = getInvalidCount(scrapes);
  
  return { ready, error, running, invalid, total: scrapes.length };
};

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp: string): string => {
  return new Date(timestamp).toLocaleString();
};

/**
 * Format relative time
 */
export const formatRelativeTime = (timestamp: string): string => {
  const now = new Date();
  const time = new Date(timestamp);
  const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays}d ago`;
};

/**
 * Get badge color for status
 */
export const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'processing':
      return 'default';
    case 'pending':
      return 'secondary';
    default:
      return 'outline';
  }
};

/**
 * Get badge color for verification status
 */
export const getVerificationBadgeVariant = (verified: boolean | null) => {
  if (verified === true) return 'success';
  if (verified === false) return 'destructive';
  return 'secondary';
};

/**
 * Get verification badge text
 */
export const getVerificationBadgeText = (verified: boolean | null) => {
  if (verified === true) return 'Valid';
  if (verified === false) return 'Invalid';
  return 'Not Verified';
};

/**
 * Validate URL format
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Extract domain from URL
 */
export const extractDomain = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Get scrapes that can be verified (have screenshot URLs and aren't verified yet)
 */
export const getVerifiableScrapes = (scrapes: Scrape[]): Scrape[] => {
  return scrapes.filter(
    scrape => (scrape.cloudinary_url || scrape.screenshot_url) && 
             scrape.verified === null && 
             scrape.status === 'ready'
  );
};
